/**
 * localStorage layout for custom backgrounds — one small index plus one pair of keys per image.
 *
 * **Split on purpose.** A single JSON blob holding every data URL would mean parsing ~2M characters
 * at slice init on every page load, for a feature most users never touch. This way boot parses a
 * ~600-byte index and an image's ~165K-character data URL is read only when something actually needs
 * it — still synchronously, which is the whole reason we're on localStorage rather than IndexedDB.
 */
export const INDEX_KEY = 'customVideoBackgrounds';
export const IMAGE_KEY_PREFIX = 'customVideoBackground:';

export const imageKey = (id: string) => `${IMAGE_KEY_PREFIX}${id}:image`;
export const thumbnailKey = (id: string) => `${IMAGE_KEY_PREFIX}${id}:thumb`;

/**
 * Two independent guards, because they mean different things — to the user ("remove one to add
 * another" vs "these images are unusually large") and to us (`custom_limit_reached` is the telemetry
 * that would justify moving to async storage; `custom_budget_exceeded` says the encoder is too
 * generous).
 *
 * The character budget is the important one. localStorage is a **shared, per-origin** resource: auth
 * (`modules/authentication/utils/magicLinkStorage`), device preferences and the background selection
 * all live in the same ~2.5M characters (5 MiB counted in UTF-16 — the conservative floor; some
 * Chrome builds allow double, but don't design against that). Filling it doesn't just fail our write,
 * it fails *theirs* — which is why this is a hard pre-write check rather than only a try/catch around
 * `setItem`. 2M leaves ~500K of headroom; everything else in the app currently uses under 10 KB.
 *
 * At ~165K characters per image (1280x720 WebP q0.82, base64-encoded) plus ~9K for its thumbnail,
 * ~11 images would fit. We stop at 8.
 */
export const MAX_CUSTOM_BACKGROUNDS = 8;
export const CUSTOM_BACKGROUNDS_CHAR_BUDGET = 2_000_000;
